/**
 * Client-side model of `ontology-tree.json` (the stripped hierarchy served from
 * the CDN) plus the inheritance logic that answers, for a file's ontology term,
 * "does this have a web viewer?" and "which icon should it show?".
 *
 * `icon` is inheritable down the DAG: a term with no icon of its own takes the
 * nearest ancestor's.
 *
 * `viewerSupported` is inheritable only as a *fallback*. The exporter stamps it
 * explicitly on every term, and an explicit value — `true` or `false` — is
 * authoritative for that term; the ancestor walk applies only where the flag is
 * absent, which keeps older published trees working. See `isViewerSupported`.
 *
 * Shape produced by `OntologyMappingExporter#exportOntologyTree` (schema v1).
 */

export interface OntologyTreeNode {
  label: string
  /** Human-readable definition of the term, when the exporter had a real one to publish. */
  description?: string
  parents: string[]
  viewerSupported?: boolean
  icon?: string
}

export interface OntologyTreeMetadata {
  schemaVersion?: number
  generated?: string
  termCount?: number
  ontologies?: Array<{name?: string; prefix?: string; version?: string}>
}

export interface OntologyTree {
  _metadata?: OntologyTreeMetadata
  terms: Record<string, OntologyTreeNode>
}

/**
 * Precomputed lookup over an {@link OntologyTree} with memoized DAG walks for
 * effective viewer-support and effective icon. Build once per tree (see the
 * `useOntologyTree` hook) and reuse across every file row.
 */
export class OntologyIndex {
  private readonly terms: Record<string, OntologyTreeNode>
  private readonly supportedCache = new Map<string, boolean>()
  private readonly iconCache = new Map<string, string | undefined>()

  constructor(tree: OntologyTree | undefined | null) {
    this.terms = tree?.terms ?? {}
  }

  has(uri: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.terms, uri)
  }

  /**
   * The raw node for a term (its own `label`/`description`, not inherited from
   * ancestors — unlike {@link effectiveIcon}, every term is stamped with its own
   * label by the exporter), or undefined if the URI is not in the tree.
   */
  term(uri: string): OntologyTreeNode | undefined {
    return this.terms[uri]
  }

  /**
   * The single best known term among `uris` for display purposes (e.g. a file's tooltip
   * headline), or undefined if none are known.
   *
   * Prefers a `format_` term over any other category (chiefly `data_`) when a file's tags
   * carry both: a format term names the concrete file type (e.g. "Krona Chart"), while a
   * same-file data term is the more generic content classification it's linked to via
   * `isFormatOf` (e.g. "Taxonomic Classification Data") — real tagged files commonly carry
   * both, and the format is what a user actually wants to see first. Falls back to the
   * first known URI, in `uris` order, when no format term is present.
   */
  preferredTerm(uris: string[]): {uri: string; node: OntologyTreeNode} | undefined {
    const known: Array<{uri: string; node: OntologyTreeNode}> = []
    for (const uri of uris) {
      const node = this.term(uri)
      if (node) {
        known.push({uri, node})
      }
    }
    return known.find((entry) => /:format_/.test(entry.uri)) ?? known[0]
  }

  /**
   * Whether a file carrying this term can be opened in a web viewer.
   *
   * An explicit `viewerSupported` on the term itself is authoritative — `false`
   * included. The exporter now stamps every term explicitly, because for `.box`
   * objects support is per concrete object class and does *not* flow to
   * siblings: `omicsbox:format_0852` ("Web chart format") is the ontology parent
   * of the Venn and SQANTI3 format terms, whose classes are not `WebChart`
   * subtypes, so inheriting its `true` would be wrong.
   *
   * Only when the term carries no flag at all do we fall back to the ancestor
   * walk, which keeps older published trees (and the raw-format subtree, where
   * inheritance is genuinely correct) working. Unknown terms → false.
   */
  isViewerSupported(uri: string): boolean {
    return this.walkSupported(uri, new Set<string>())
  }

  /** Nearest self-or-ancestor icon id (e.g. `bam.svg`), or undefined if none. */
  effectiveIcon(uri: string): string | undefined {
    return this.walkIcon(uri, new Set<string>())
  }

  private walkSupported(uri: string, visiting: Set<string>): boolean {
    const cached = this.supportedCache.get(uri)
    if (cached !== undefined) {
      return cached
    }
    // Guard against cycles in a malformed DAG.
    if (visiting.has(uri)) {
      return false
    }
    visiting.add(uri)
    const node = this.terms[uri]
    let result = false
    if (node) {
      if (node.viewerSupported !== undefined) {
        // Explicit flag wins outright — an explicit `false` stops the walk rather than deferring
        // to a supported ancestor.
        result = node.viewerSupported
      } else {
        result = (node.parents ?? []).some((p) => this.walkSupported(p, visiting))
      }
    }
    visiting.delete(uri)
    this.supportedCache.set(uri, result)
    return result
  }

  private walkIcon(uri: string, visiting: Set<string>): string | undefined {
    if (this.iconCache.has(uri)) {
      return this.iconCache.get(uri)
    }
    if (visiting.has(uri)) {
      return undefined
    }
    visiting.add(uri)
    const node = this.terms[uri]
    let result: string | undefined
    if (node) {
      if (node.icon) {
        result = node.icon
      } else {
        for (const p of node.parents ?? []) {
          result = this.walkIcon(p, visiting)
          if (result) {
            break
          }
        }
      }
    }
    visiting.delete(uri)
    this.iconCache.set(uri, result)
    return result
  }
}
